// Description: This component displays a row of event information, including the event image, title, dates, composers, and venue. It also includes a "More" button for additional details.
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";

export const OperaContext = createContext(null);

export const OperaProvider = ({ children }) => {
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [unpublishedEvents, setUnpublishedEvents] = useState([]);
  const [hiddenEvents, setHiddenEvents] = useState([]);
  const [famousPerformers, setFamousPerformers] = useState([]);
  const [allFamousPerformers, setAllFamousPerformers] = useState([]);
  const [composers, setComposers] = useState([]);
  const [allComposers, setAllComposers] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allCrews, setAllCrews] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [prePopulateEventData, setPrePopulateEventData] = useState({
    venueId: null,
    city: null,
  });

  //for state persistence
  const [eventFormData, setEventFormData] = useState(null);
  const [eventFormDataEdit, setEventFormDataEdit] = useState(null);
  const [venueFormData, setVenueFormData] = useState(null);
  const [venueFormDataEdit, setVenueFormDataEdit] = useState(null);
  const [hotelFormData, setHotelFormData] = useState(null);
  const [crewFormData, setCrewFormData] = useState(null);
  const [performerFormData, setPerformerFormData] = useState(null);
  const [composerFormData, setComposerFormData] = useState(null);

  //Track if initial data has been fetched
  const initialFetchCompleted = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        venuesRes,
        eventsRes,
        performersRes,
        allPerformersRes,
        composersRes,
        allComposersRes,
        allCrewsRes,
        hotelsRes, // Added hotels fetch
        citiesRes,
      ] = await Promise.all([
        axios.get("/api/venues"),
        axios.get("/api/events"),
        axios.get("/api/famous-performers"),
        axios.get("/api/famous-performers/all"),
        axios.get("/api/composers"),
        axios.get("/api/composers/all"),
        axios.get("/api/crews/all"),
        axios.get("/api/hotels"), // Fetch hotels
        axios.get("/api/cities"),
      ]);

      // Filter out invalid events
      const allEvents = eventsRes.data.filter(
        (event) =>
          event &&
          event._id &&
          event.date_times &&
          Array.isArray(event.date_times)
      );

      // Update state
      setVenues(venuesRes.data);
      setEvents(allEvents.filter((e) => e.published && !e.hidden));
      setUnpublishedEvents(allEvents.filter((e) => !e.published && !e.hidden));
      setHiddenEvents(allEvents.filter((e) => e.hidden));
      setFamousPerformers(performersRes.data);
      setAllFamousPerformers(allPerformersRes.data);
      setComposers(composersRes.data);
      setAllComposers(allComposersRes.data);
      setAllCities(citiesRes.data);
      setAllCrews(allCrewsRes.data);
      setHotels(hotelsRes.data.hotels); // Set hotels
    } catch (err) {
      console.error("Error fetching data:", err.message);
      if (err.response) {
        console.error("Response status:", err.response.status);
      }
    } finally {
      setLoading(false);
      setDataFetched(true);
      initialFetchCompleted.current = true;
    }
  }, []);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        venuesRes,
        allEventsRes,
        unpublishedEventsRes,
        hiddenEventsRes,
        performersRes,
        allPerformersRes,
        composersRes,
        allComposersRes,
        allCrewsRes,
        citiesRes,
        hotelsRes,
      ] = await Promise.all([
        axios.get("/api/venues/admin/all", { withCredentials: true }),
        axios.get("/api/events/all", { withCredentials: true }),
        axios.get("/api/events/unpublished", { withCredentials: true }),
        axios.get("/api/events/hidden", { withCredentials: true }),
        axios.get("/api/famous-performers", { withCredentials: true }),
        axios.get("/api/famous-performers/all", { withCredentials: true }),
        axios.get("/api/composers", { withCredentials: true }),
        axios.get("/api/composers/all", { withCredentials: true }),
        axios.get("/api/crews/all", { withCredentials: true }),
        axios.get("/api/cities", { withCredentials: true }),
        axios.get("/api/hotels", { withCredentials: true }),
      ]);

      const allEvents = allEventsRes.data.filter(
        (event) =>
          event &&
          event._id &&
          event.date_times &&
          Array.isArray(event.date_times)
      );

      const unpublishedEvents = unpublishedEventsRes.data.filter(
        (event) =>
          event &&
          event._id &&
          event.date_times &&
          Array.isArray(event.date_times)
      );

      const hiddenEvents = hiddenEventsRes.data.filter(
        (event) =>
          event &&
          event._id &&
          event.date_times &&
          Array.isArray(event.date_times)
      );

      setVenues(venuesRes.data);
      setEvents(allEvents.filter((e) => e.published && !e.hidden));
      setUnpublishedEvents(unpublishedEvents);
      setHiddenEvents(hiddenEvents);
      setFamousPerformers(performersRes.data);
      setAllFamousPerformers(allPerformersRes.data);
      setComposers(composersRes.data);
      setAllComposers(allComposersRes.data);
      setAllCities(citiesRes.data);
      setAllCrews(allCrewsRes.data);
      setHotels(hotelsRes.data.hotels);
    } catch (err) {

      if (err.response) {
        console.error("Response status:", err.response.status);
      }

      await fetchData();
    } finally {
      setLoading(false);
      setDataFetched(true);
      initialFetchCompleted.current = true; // Mark initial fetch as completed
    }
  }, [fetchData]);

  useEffect(() => {
    if (!initialFetchCompleted.current) {
      fetchData();
    } else {
      console.log("OperaContext: Data already fetched, skipping fetch.");
    }
  }, [fetchData]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      venues,
      setVenues,
      events,
      setEvents,
      unpublishedEvents,
      setUnpublishedEvents,
      hiddenEvents,
      setHiddenEvents,
      famousPerformers,
      setFamousPerformers,
      allFamousPerformers,
      setAllFamousPerformers,
      composers,
      setComposers,
      allComposers,
      setAllComposers,
      allCities,
      setAllCities,
      allCrews,
      setAllCrews,
      hotels, // Added hotels to context
      setHotels,
      loading,
      dataFetched,
      fetchData,
      fetchAdminData,
      prePopulateEventData,
      setPrePopulateEventData,
      eventFormData,
      setEventFormData,
      eventFormDataEdit,
      setEventFormDataEdit,
      venueFormData,
      setVenueFormData,
      venueFormDataEdit,
      setVenueFormDataEdit,
      hotelFormData,
      setHotelFormData,
      crewFormData,
      setCrewFormData,
      performerFormData,
      setPerformerFormData,
      composerFormData,
      setComposerFormData,
    }),
    [
      venues,
      events,
      unpublishedEvents,
      hiddenEvents,
      famousPerformers,
      allFamousPerformers,
      composers,
      allComposers,
      allCities,
      allCrews,
      hotels,
      loading,
      dataFetched,
      fetchData,
      fetchAdminData,
      prePopulateEventData,
      eventFormData,
      venueFormData,
      hotelFormData,
      crewFormData,
      performerFormData,
      composerFormData,
      eventFormDataEdit,
      venueFormDataEdit,
    ]
  );

  return (
    <OperaContext.Provider value={contextValue}>
      {children}
    </OperaContext.Provider>
  );
};

export default OperaProvider;
